/**
 * Runtime flame settings (q-npc-props-adoption-and-flame): one pure decision per session,
 * taken from the quality profile and the URL, so the lane and the tests read the same rule.
 *
 * - `enabled` — draw runtime flames at all. `?flames=off` shows every asset AS AUTHORED: no
 *   hide rule runs and no flame is added (the before/after switch for measurement).
 * - `lights` — the size of the flickering point-light pool. Desktop gets
 *   `FLAME_LIGHT_CAP_DESKTOP`; the low profile (mobile, `?quality=low`) gets NONE: every
 *   extra point light is paid by every lit fragment on screen. `?flameLights=N` overrides
 *   it (clamped to `0..FLAME_LIGHT_CAP_MAX`) for measurement.
 * - `detail` — `'lite'` on the low profile: the flame drops its tongue wobble and second
 *   flicker octave (a cheaper fragment); `'full'` otherwise.
 *
 * Node-safe (no `three`), like quality.ts.
 */

/** Point lights the desktop profile lends to the nearest lit flames. */
export const FLAME_LIGHT_CAP_DESKTOP = 2;
/** The most point lights a `?flameLights=` override may ask for. */
export const FLAME_LIGHT_CAP_MAX = 4;

export type FlameDetail = 'full' | 'lite';

export interface FlameConfig {
  readonly enabled: boolean;
  readonly lights: number;
  readonly detail: FlameDetail;
}

function param(search: string, name: string): string | null {
  try {
    return new URLSearchParams(search).get(name);
  } catch {
    return null;
  }
}

/** Resolve the session's flame settings. Pure: the same inputs give the same config. */
export function resolveFlameConfig(profile: { readonly low: boolean }, search = ''): FlameConfig {
  const flames = param(search, 'flames');
  const enabled = !(flames === 'off' || flames === '0' || flames === 'false');
  let lights = profile.low ? 0 : FLAME_LIGHT_CAP_DESKTOP;
  const override = param(search, 'flameLights');
  if (override !== null && override.trim() !== '') {
    const n = Number(override);
    if (Number.isFinite(n)) lights = Math.max(0, Math.min(FLAME_LIGHT_CAP_MAX, Math.floor(n)));
  }
  return { enabled, lights: enabled ? lights : 0, detail: profile.low ? 'lite' : 'full' };
}
